import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationStart, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  AlertModule,
  BadgeModule,
  ButtonModule,
  CardBodyComponent,
  CardModule,
  GridModule,
  SpinnerModule,
  TableModule
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { UserService, type DashboardStats } from 'app';
import { AuthService } from 'auth';
import { CardAccountService } from 'credo';
import { I18nService, TranslatePipe, TranslateService, TranslationModule } from 'i18n';
import { ImageLink, Media, MediaCardComponent, MediaLink, MediaService, MediaViewerOverlayService, PdfMedia } from 'media';
import { CardAccount, CurrencyAmountPipe, Logger, type User, type UserPool } from 'shared';
import { UserPoolService } from '../../../services/user-pool.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, pipe, take } from 'rxjs';

@Component({
  selector: 'main-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AlertModule,
    BadgeModule,
    ButtonModule,
    CardModule,
    GridModule,
    IconModule,
    SpinnerModule,
    TableModule,
    TranslationModule,
    CardBodyComponent,
    TranslatePipe,
    MediaCardComponent,
    IconDirective,
    CurrencyAmountPipe
  ],
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainPageComponent {
  protected logger = new Logger('MainPageComponent');

  private userService = inject(UserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private userPoolService = inject(UserPoolService);
  private cardAccountService = inject(CardAccountService);
  private mediaService = inject(MediaService);
  private mediaViewer = inject(MediaViewerOverlayService);
  private i18n = inject(I18nService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  user = signal<User | null>(null);
  // Per-currency eWallet balances for the dashboard widget.
  balances = computed(() => this.user()?.balances ?? []);
  stats = signal<DashboardStats | null>(null);
  userPools = signal<UserPool[]>([]);
  userCards = signal<CardAccount[]>([]);
  domainName = signal<string>('');
  loading = signal(true);
  poolsLoading = signal(false);
  cardsLoading = signal(false);
  error = signal<string | null>(null);

  // Email stats (not used in this version, but kept)
  confirmedEmailsCount = signal(0);
  poolsRequiringEmails = signal(0);
  requiredEmailsCount = signal(0);

  // Latest media
  media = signal<Media | null>(null);

  // Admin fee status
  adminFeePaidUntil = signal<Date | null>(null);
  adminFeeExpiringSoon = signal(false);
  adminFeeExpired = signal(false);
  showAdminFeeStatus = signal(false);

  // Presale countdown (ends Nov 15, 2025 23:59 EST)
  presaleEndDate = new Date('2025-11-15T23:59:00-05:00');
  countdownDays = signal(0);
  countdownHours = signal(0);
  countdownMinutes = signal(0);
  countdownSeconds = signal(0);
  presaleEnded = signal(false);

  currentLang = signal<string>('en');

  // PDF modal state
  pdfModalVisible = signal(false);
  pdfModalTitle = signal('');
  pdfUrl = signal<SafeResourceUrl | null>(null);

  // Cards without initial payment (computed)
  cardsWithoutInitialPayment = computed(() =>
    this.userCards().filter(card => !card.initialpayment_done).length
  );

  constructor() {
    // Sync current language
    effect(() => {
      this.currentLang.set(this.i18n.getLanguage());
    });

    // Load all data
    this.loadUser();
    this.loadDashboardStats();
    this.loadUserPools();
    this.loadUserCards();
    this.loadLatestInfoMedia();
    this.loadDomainInfo();

    // Countdown effect with cleanup
    effect((onCleanup) => {
      this.updateCountdown();
      const interval = setInterval(() => this.updateCountdown(), 1000);
      onCleanup(() => clearInterval(interval));
    });
  }

  // ────────────────────────────────────────────────
  // Data loading methods
  // ────────────────────────────────────────────────
  private loadUser(): void {
    this.authService.getUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.user.set(user);
        if (user && user.level >= 2) {
          this.checkAdminFeeStatus(user);
        } else {
          this.showAdminFeeStatus.set(false);
        }
      });

    // Refresh from the server so balances (per currency) are current, not the
    // login-time snapshot. The getUser$ subscription above picks up the update.
    this.authService.refreshUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: err => this.logger.error('refreshUser failed', err) });
  }

  private checkAdminFeeStatus(user: User): void {
    if (!user.admin_fee_paid_until) {
      this.showAdminFeeStatus.set(false);
      return;
    }

    this.showAdminFeeStatus.set(true);
    const paidUntil = new Date(user.admin_fee_paid_until);
    this.adminFeePaidUntil.set(paidUntil);

    const now = new Date();
    const daysLeft = Math.ceil((paidUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    this.adminFeeExpired.set(daysLeft < 0);
    this.adminFeeExpiringSoon.set(daysLeft > 0 && daysLeft <= 10);
  }

  private loadDashboardStats(): void {
    this.loading.set(true);
    this.error.set(null);
    this.userService.getDashboardStats()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: data => {
        this.stats.set(data);
        this.loading.set(false);
      },
      error: err => {
        this.logger.error('Dashboard stats error:', err);
        this.error.set(err.error?.error || 'Failed to load dashboard statistics');
        this.loading.set(false);
      }
    });
  }

  private loadUserPools(): void {
    this.poolsLoading.set(true);
    this.userPoolService.getMyPools()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.userPools.set(response.user_pools.sort((a, b) => b.pool_id - a.pool_id));
            this.poolsLoading.set(false);
          },
          error: err => {
            this.logger.error('User pools error:', err);
            this.poolsLoading.set(false);
          }
        });
  }

  private loadUserCards(): void {
    this.cardsLoading.set(true);
    this.cardAccountService.getMyCardAccounts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.userCards.set(response.card_accounts);
          this.cardsLoading.set(false);
        },
        error: err => {
          this.logger.error('User cards error:', err);
          this.cardsLoading.set(false);
        }
      });
  }

  private loadLatestInfoMedia(): void {
    this.mediaService
      .loadMedia(this.i18n.getLanguage(), 'info', '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        if (!items?.length) {
          this.media.set(null);
          return;
        }

        const latest = items.reduce((prev, curr) => {
          const prevTime = prev.timeUpdated ?? prev.timeCreated ?? 0;
          const currTime = curr.timeUpdated ?? curr.timeCreated ?? 0;
          return currTime > prevTime ? curr : prev;
        });

        this.media.set(latest);
      });
  }

  private loadDomainInfo(): void {
    this.http.get<{ domain_id: number; name: string }>('/auth/domain_info')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.domainName.set(res.name);
        },
        error: err => {
          this.logger.error('Domain info error:', err);
          this.domainName.set('');
        }
      });
  }

  // ────────────────────────────────────────────────
  // Countdown
  // ────────────────────────────────────────────────
  private updateCountdown(): void {
    const distance = this.presaleEndDate.getTime() - Date.now();
    if (distance < 0) {
      this.presaleEnded.set(true);
      return;
    }

    this.countdownDays.set(Math.floor(distance / (1000 * 60 * 60 * 24)));
    this.countdownHours.set(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    this.countdownMinutes.set(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
    this.countdownSeconds.set(Math.floor((distance % (1000 * 60)) / 1000));
  }

  // ────────────────────────────────────────────────
  // PDF & Media
  // ────────────────────────────────────────────────
  openPdfModal(type: 'provisions' | 'license' | 'cards'): void {
    let pdfPath = '';
    let titleKey = '';

    if (type === 'provisions') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/commisison_overview_1.0_de.pdf'
        : '/assets/commisison_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_provisions_title';
    } else if (type === 'license') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/FAC3LESS_Pool-Lizenzsystem_1.1.pdf'
        : '/assets/FAC3LESS_PoolLicenseSystem_1.1_en.pdf';
      titleKey = 'dashboard_pdf_license_title';
    } else if (type === 'cards') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/card_overview_1.0_de.pdf'
        : '/assets/card_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_cards_title';
    }

    if (pdfPath) {
      this.pdfModalTitle.set(this.translate.translate(titleKey));

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const pdfLink: MediaLink = { src: `${baseUrl}${pdfPath}`, mimeType: 'application/pdf', size: 0 };
      const cover: ImageLink = {
        src: `${window.location.origin}/assets/images/default-media.jpg`,
        mimeType: 'image/jpg',
        width: 0,
        height: 0
      };

      const media: PdfMedia = {
        id: '0',
        name: this.pdfModalTitle(),
        type: 'pdf',
        links: [pdfLink],
        cover,
        properties: { cover: pdfLink },
        timeUpdated: Date.now(),
        trackReactions: false
      };

      this.openMediaViewer(media);
    }
  }

  openMediaViewer(media: Media): void {
    this.mediaViewer.open({ media }).then(overlayRef => {
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        overlayRef.destroy();
      });

      overlayRef.onDestroy.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        // optional cleanup
      });

      overlayRef.onAction.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ action, parameter }) => {
        if (action === 'played') {
          const playedMedia = parameter as Media;
          if (playedMedia.trackReactions) {
            this.mediaService.setReaction(playedMedia.id, 'played')
              .then(() => this.loadLatestInfoMedia());
          }
        }
      });
    }).catch(err => {
      this.logger.error('Failed to open media viewer:', err);
    });
  }

  // ────────────────────────────────────────────────
  // Formatting & helpers (unchanged logic)
  // ────────────────────────────────────────────────
  formatCurrency(value: number): string {
    const formatted = new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return `${formatted} USD`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  formatAdminFeeDate(): string {
    const date = this.adminFeePaidUntil();
    if (!date) return '';
    return date.toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }

  getPercentage(pool: UserPool): number {
    if (pool.max_balance === 0) return 0;
    return Math.round((pool.turnover / pool.max_balance) * 100);
  }

  getCardsWithoutInitialPayment(): number {
    return this.userCards().filter(card => !card.initialpayment_done).length;
  }

  getCardTypeLabel(cardType: string): string {
    const labels: Record<string, string> = {
      silber: this.translate.translate('dashboard_card_type_silber'),
      gold: this.translate.translate('dashboard_card_type_gold'),
      platin: this.translate.translate('dashboard_card_type_platin'),
      m3: this.translate.translate('dashboard_card_type_m3')
    };
    return labels[cardType] || cardType;
  }

  getCardTypeBadgeColor(cardType: string): string {
    const colors: Record<string, string> = {
      silber: 'secondary',
      gold: 'warning',
      platin: 'info',
      m3: 'primary'
    };
    return colors[cardType] || 'dark';
  }

  getLevelBadgeUrl(level: number): string {
    return `/assets/images/badge${level || 0}.png`;
  }

  getLevelName(level: number): string {
    const levelNames: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_name'),
      1: this.translate.translate('dashboard_level_1_name'),
      2: this.translate.translate('dashboard_level_2_name'),
      3: this.translate.translate('dashboard_level_3_name'),
      4: this.translate.translate('dashboard_level_4_name'),
      5: this.translate.translate('dashboard_level_5_name'),
      6: this.translate.translate('dashboard_level_6_name'),
      7: this.translate.translate('dashboard_level_7_name', { appName: this.translate.translate('app.name') })
    };
    return levelNames[level] || this.translate.translate('dashboard_level_unknown');
  }

  getLevelDescription(level: number): string {
    const descriptions: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_desc'),
      1: this.translate.translate('dashboard_level_1_desc'),
      2: this.translate.translate('dashboard_level_2_desc'),
      3: this.translate.translate('dashboard_level_3_desc'),
      4: this.translate.translate('dashboard_level_4_desc'),
      5: this.translate.translate('dashboard_level_5_desc'),
      6: this.translate.translate('dashboard_level_6_desc'),
      7: this.translate.translate('dashboard_level_7_desc')
    };
    return descriptions[level] || this.translate.translate('dashboard_level_unknown_desc');
  }

  onBadgeImageError(event: Event): void {
    (event.target as HTMLImageElement).src = '/assets/images/badge0.png';
  }
}